import type { StagePosition } from "@gettactic/map-builder/src/components/map/types";
import React from "react";
import { viewportTransform } from "../render/MapSvg";

const MIN_SCALE = 0.1;
const MAX_SCALE = 5;

export interface ViewerPanZoomOptions {
  /** The scrolling/dragging surface. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** The pan/zoom group inside the svg, updated directly while moving. */
  rootRef: React.RefObject<SVGGElement | null>;
  positionRef: React.RefObject<StagePosition>;
  commit: (position: StagePosition) => void;
  enabled?: boolean;
}

/**
 * Pan by dragging or two-finger scrolling, zoom about the cursor with the
 * wheel (Cmd/Ctrl) or pinch, as the Konva view canvas does. Moves write
 * the group transform directly and commit to the map's stage position
 * when the gesture ends.
 */
export const useViewerPanZoom = ({
  containerRef,
  rootRef,
  positionRef,
  commit,
  enabled = true
}: ViewerPanZoomOptions): void => {
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;
    let live = positionRef.current;
    let drag: {
      client: { x: number; y: number };
      position: StagePosition;
    } | null = null;
    let wheelTimer: number | null = null;

    const apply = (position: StagePosition) => {
      live = position;
      rootRef.current?.setAttribute("transform", viewportTransform(position));
    };
    const settle = () => {
      if (wheelTimer !== null) window.clearTimeout(wheelTimer);
      wheelTimer = null;
      commit(live);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      // Spots and other click targets keep their clicks; a drag from
      // anywhere still pans.
      drag = {
        client: { x: event.clientX, y: event.clientY },
        position: positionRef.current
      };
      container.setPointerCapture(event.pointerId);
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!drag) return;
      apply({
        ...drag.position,
        x: drag.position.x + event.clientX - drag.client.x,
        y: drag.position.y + event.clientY - drag.client.y
      });
    };
    const onPointerUp = (event: PointerEvent) => {
      if (!drag) return;
      if (container.hasPointerCapture(event.pointerId))
        container.releasePointerCapture(event.pointerId);
      const moved = Math.hypot(
        event.clientX - drag.client.x,
        event.clientY - drag.client.y
      );
      drag = null;
      if (moved > 0) settle();
    };
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = container.getBoundingClientRect();
      const pointer = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
      const current = live;
      if (event.ctrlKey || event.metaKey) {
        const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
        const scale = Math.min(
          Math.max(current.scale * factor, MIN_SCALE),
          MAX_SCALE
        );
        const worldX = (pointer.x - current.x) / current.scale;
        const worldY = (pointer.y - current.y) / current.scale;
        apply({
          x: pointer.x - worldX * scale,
          y: pointer.y - worldY * scale,
          scale
        });
      } else {
        const intensity = 1 / Math.max(current.scale, 0.5);
        apply({
          ...current,
          x: current.x - event.deltaX * intensity,
          y: current.y - event.deltaY * intensity
        });
      }
      if (wheelTimer !== null) window.clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(settle, 100);
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerUp);
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      if (wheelTimer !== null) window.clearTimeout(wheelTimer);
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerUp);
      container.removeEventListener("wheel", onWheel);
    };
  }, [containerRef, rootRef, positionRef, commit, enabled]);
};
